import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, MessageCircle, Upload, PackageCheck, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { api, apiError, imgUrl, formatINR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { UpiPayBlock } from "../components/UpiPayBlock";

const STATUS_LABELS = { pending: "Payment pending", pay_on_delivery: "Pay on delivery", awaiting_verification: "Verifying payment", verified: "Payment verified", rejected: "Payment issue" };

export default function OrderSuccess() {
  const { ref } = useParams();
  const { settings } = useStore();
  const [order, setOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [proofDone, setProofDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(`anaya_order_${ref}`);
      if (saved) setOrder(JSON.parse(saved));
    } catch {}
  }, [ref]);

  if (!order) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="order-missing">
        <Seo title="Order — Anaya Fashion & Gifts" path="/order" noindex />
        <h1 className="font-display text-4xl">Order {ref}</h1>
        <p className="mt-3 text-sm font-light text-muted-foreground">We don&#8217;t have the details on this device. Track it with your phone number.</p>
        <Link to="/track" data-testid="order-track-link" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Track order</Link>
      </div>
    );
  }

  const wa = settings?.whatsapp || "919643444088";

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("phone", order.customer.phone);
      fd.append("file", file);
      await api.post(`/orders/${order.order_reference}/proof`, fd);
      setProofDone(true);
      toast.success("Proof received — we'll verify it shortly");
    } catch (err) {
      toast.error(apiError(err, "Upload failed. Try WhatsApp instead."));
    } finally {
      setUploading(false);
    }
  };

  const waText = encodeURIComponent(
    `Hello Anaya Fashion & Gifts! I just placed an order.\nOrder: ${order.order_reference}\nConfirmation Code: ${order.confirmation_code}\nTotal: ₹${order.total}\nPayment method: ${order.payment_method.replace(/_/g, " ")}\nI am attaching my payment screenshot here.`
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="order-success-page">
      <Seo title="Order Confirmed — Anaya Fashion & Gifts" path="/order" noindex />
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", duration: 0.8 }} className="text-center">
        <CheckCircle2 className="mx-auto text-green-600" size={56} />
        <h1 className="mt-4 font-display text-4xl sm:text-5xl" data-testid="order-thank-you">Thank you, {order.customer.name}!</h1>
        <p className="mt-3 text-sm font-light text-muted-foreground">Your order is placed. Keep these two codes safe:</p>
      </motion.div>

      <div className="glass-deep mt-8 grid gap-4 rounded-3xl p-6 text-center sm:grid-cols-2" data-testid="order-codes">
        <div className="rounded-2xl border border-input bg-white/70 p-4">
          <p className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase">Order number</p>
          <p className="gold-text mt-1 font-display text-2xl font-semibold tracking-[0.15em]" data-testid="order-reference">{order.order_reference}</p>
        </div>
        <div className="rounded-2xl border-2 border-primary/40 bg-secondary/50 p-4">
          <p className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase">Order confirmation code</p>
          <p className="mt-1 font-display text-2xl font-semibold tracking-[0.15em] text-primary" data-testid="confirmation-code">{order.confirmation_code}</p>
        </div>
      </div>

      <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="order-summary">
        <div className="flex items-center justify-between">
          <p className="font-display text-xl">Order summary</p>
          <span className="rounded-full bg-secondary px-3 py-1 text-[10px] tracking-widest uppercase" data-testid="order-payment-status">
            {STATUS_LABELS[proofDone ? "awaiting_verification" : order.payment_status] || order.payment_status}
          </span>
        </div>
        <ul className="mt-4 space-y-3">
          {order.items.map((i) => (
            <li key={i.product_id} className="flex items-center gap-3 text-sm font-light">
              <span className="h-12 w-10 overflow-hidden rounded-lg bg-secondary/60">
                {i.image && <img src={imgUrl(i.image)} alt="" className="h-full w-full object-cover" />}
              </span>
              <span className="flex-1 line-clamp-1">{i.name}{i.variant ? ` · Design ${i.variant}` : ""} × {i.qty}</span>
              <span>{formatINR(i.price * i.qty)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-2 border-t border-input pt-4 text-sm font-light">
          <div className="flex justify-between"><span>Subtotal</span><span data-testid="order-subtotal">{formatINR(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Shipping ({order.payment_method === "cod" ? "COD" : "prepaid"})</span><span data-testid="order-shipping">{formatINR(order.shipping || 0)}</span></div>
        </div>
        <div className="mt-3 flex justify-between border-t border-input pt-3 font-medium">
          <span>Total</span><span data-testid="order-total">{formatINR(order.total)}</span>
        </div>
        <p className="mt-4 text-xs font-light text-muted-foreground">
          Delivering to: {order.customer.address}{order.customer.city ? `, ${order.customer.city}` : ""}{order.customer.pincode ? ` ${order.customer.pincode}` : ""}
        </p>
      </div>

      {order.payment_method === "cod" && (
        <div className="mt-6 rounded-3xl border-2 border-amber-300 bg-amber-50 p-6" data-testid="cod-reminder">
          <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-amber-800">
            <AlertTriangle size={16} /> COD orders require advance shipping payment
          </p>
          <p className="mt-2 text-xs leading-relaxed text-amber-900/80">
            Pay <strong>₹120 shipping charges</strong> in advance, then send your confirmation code <strong>{order.confirmation_code}</strong> and the shipping payment screenshot on WhatsApp. The remaining product amount is payable on delivery. Your order is confirmed and dispatched only after successful verification.
          </p>
        </div>
      )}

      <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="whatsapp-confirm-card">
        <p className="font-display text-xl">Confirm your order on WhatsApp</p>
        <p className="mt-2 text-sm font-light leading-relaxed text-muted-foreground">
          {order.payment_method === "cod"
            ? "Send your confirmation code and the advance shipping payment screenshot."
            : "Send your confirmation code along with your full payment screenshot."}{" "}
          Tapping the button opens WhatsApp with your order details pre-filled — you attach the screenshot and press send yourself.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a data-testid="whatsapp-confirm-btn" href={`https://wa.me/${wa}?text=${waText}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-xs font-semibold tracking-[0.15em] text-white uppercase shadow-lg transition-transform hover:scale-[1.03]">
            <MessageCircle size={16} /> Send order confirmation on WhatsApp
          </a>
          <span className="text-xs font-light text-muted-foreground">to {wa.replace(/^91/, "")}</span>
        </div>
        <p className="mt-3 rounded-xl bg-accent/40 px-4 py-2.5 text-[11px] font-light text-accent-foreground">
          Clicking the button does not confirm payment — our team verifies every payment manually before dispatch.
        </p>
      </div>

      {order.payment_method !== "cod" && !proofDone && (
        <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="proof-upload-card">
          <p className="font-display text-xl">Or upload your payment proof here</p>
          {order.payment_method === "upi" && <UpiPayBlock settings={settings} compact />}
          {order.payment_method === "bank_transfer" && (
            <p className="mt-2 text-sm font-light">{settings?.bank_name} · A/C <span className="font-medium">{settings?.bank_account_number}</span> · IFSC <span className="font-medium">{settings?.bank_ifsc}</span></p>
          )}
          <div className="mt-4">
            <button data-testid="proof-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-xs font-medium tracking-[0.2em] text-white uppercase transition-colors hover:bg-foreground disabled:opacity-50">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload payment proof"}
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="proof-file-input" onChange={uploadProof} />
        </div>
      )}

      {proofDone && (
        <div className="glass mt-6 flex items-center gap-3 rounded-3xl p-6" data-testid="proof-success">
          <PackageCheck className="text-green-600" size={24} />
          <p className="text-sm font-light">Payment proof received. We&#8217;ll verify it and confirm your order shortly.</p>
        </div>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <Link to="/track" data-testid="order-track-cta" className="rounded-full border border-foreground/20 px-7 py-3 text-xs tracking-[0.2em] uppercase hover:border-primary hover:text-primary">Track order</Link>
        <Link to="/shop" data-testid="order-continue-cta" className="rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Keep shopping</Link>
      </div>
    </div>
  );
}
